import React, { useEffect, useMemo, useState } from "react";
import { API } from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import { AdministrativeProposalsPanel } from "./components/AdministrativeProposalsPanel";
import { EhpadCreditsTable } from "./components/EhpadCreditsTable";
import { GuardianCuratorTable } from "./components/GuardianCuratorTable";
import { useAdministrationImport, useAdministrationReport, useAdministrativeProposals, usePatientDocuments } from "./hooks/useAdministrationTools";
import { useEhpadCredits, useGuardianCurators } from "./hooks/useEditableTable";
import { useLoadedPatients, useResidentSaving } from "./hooks/useResidentEditing";
import { filterResidentRows, useEhpads, useResidentFilters, useResidentModals, useResidentPages } from "./hooks/useResidents";
import { ADMIN_API_CONFIG, WITHOUT_SUBCATEGORY_FILTER } from "./model/administrationConstants";
import { administrationPayload } from "./model/administrationRules";
import { centeredStyle, errorStyle } from "./styles/administrationModalStyles";
import { renderHorizontalTable } from "./views/HorizontalTableView";
import { renderImportPanel } from "./views/ImportPanelView";
import { renderModals } from "./views/ModalsView";
import { renderResidentToolbar } from "./views/ResidentToolbarView";
import { renderTabs } from "./views/TabsView";
import { renderVerticalWorkspace } from "./views/VerticalWorkspaceView";

const samePatient = (a, b) => String(a) === String(b);

// Résident sélectionné : il doit rester visible dans la liste filtrée, sinon on prend le premier.
function useResidentSelection({ filteredRows, selectedPatientId, setSelectedPatientId, subcategory, setSubcategory, subcategories }) {
    useEffect(() => {
        if (!subcategory || subcategory === WITHOUT_SUBCATEGORY_FILTER || subcategories.includes(subcategory)) return;
        setSubcategory("");
    }, [setSubcategory, subcategories, subcategory]);
    useEffect(() => {
        if (filteredRows.length === 0) {
            setSelectedPatientId("");
            return;
        }
        setSelectedPatientId(current => (
            filteredRows.some(row => samePatient(row.patientId, current)) ? current : String(filteredRows[0].patientId)
        ));
    }, [filteredRows, setSelectedPatientId]);
    return filteredRows.find(row => samePatient(row.patientId, selectedPatientId)) || null;
}

// Actions sur une fiche hors enregistrement du brouillon : remarque PDA archivée, identité, suppression, création.
function useResidentActions({ ehpadId, pages, loaded, saving, modals, setSelectedPatientId, setSearch, onError }) {
    const [pdaHistoryRefreshKey, setPdaHistoryRefreshKey] = useState(0);
    const { replaceLoadedPatient, setDrafts, cancelRow } = loaded;

    const archivePdaComment = async row => {
        const comment = String(row?.pdaProductionRemark || "").trim();
        if (!row?.patientId || !comment) return;
        saving.setSaving(row.patientId, true);
        onError("");
        try {
            const response = await API.post(
                `/administration/patients/${row.patientId}/pda-production-remark/archive`,
                { version: row.version ?? null, comment },
                ADMIN_API_CONFIG
            );
            replaceLoadedPatient(response.data);
            setDrafts(current => {
                const draft = current[row.patientId];
                if (!draft) return current;
                const { administrationId, version, updatedAt, updatedBy } = response.data;
                return {
                    ...current,
                    [row.patientId]: {
                        ...draft, pdaProductionRemark: null, administrationId, version, updatedAt, updatedBy,
                        _base: { ...(draft._base || response.data), ...response.data },
                    },
                };
            });
            setPdaHistoryRefreshKey(current => current + 1);
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible d'ajouter le commentaire à l'historique.");
        } finally {
            saving.setSaving(row.patientId, false);
        }
    };

    const savePatientIdentity = async (row, identity) => {
        const payload = Object.assign(administrationPayload(row), identity);
        const response = await API.put(`/administration/patients/${row.patientId}`, payload, ADMIN_API_CONFIG);
        replaceLoadedPatient(response.data);
        setDrafts(current => (
            current[row.patientId]
                ? { ...current, [row.patientId]: { ...response.data, ...current[row.patientId], ...identity, version: response.data.version } }
                : current
        ));
    };

    const deletePatient = async patientId => {
        await API.delete(`/administration/patients/${patientId}`, ADMIN_API_CONFIG);
        pages.setRows(current => current.filter(row => row.patientId !== patientId));
        pages.setTotal(current => Math.max(current - 1, 0));
        cancelRow(patientId);
    };

    const createPatient = async payload => {
        const response = await API.post(`/administration/ehpads/${ehpadId}/patients`, payload, ADMIN_API_CONFIG);
        pages.setRows(current => [...current, response.data].sort((a, b) =>
            `${a.nom || ""} ${a.prenom || ""}`.localeCompare(`${b.nom || ""} ${b.prenom || ""}`, "fr")
        ));
        pages.setTotal(current => current + 1);
        setSelectedPatientId(String(response.data.patientId));
        setSearch("");
        modals.setAddPatientOpen(false);
    };

    return { pdaHistoryRefreshKey, archivePdaComment, savePatientIdentity, deletePatient, createPatient };
}

// Résidents de l'EHPAD choisi : chargement par pages, filtres, sélection, brouillons et fenêtres de la fiche.
function useResidentWorkspace({ needsPharmacy, pharmacyId, onError }) {
    const { ehpads, ehpadId, setEhpadId } = useEhpads({ needsPharmacy, pharmacyId, onError });
    const [residentViewMode, setResidentViewMode] = useState("vertical");
    const [selectedPatientId, setSelectedPatientId] = useState("");
    const filters = useResidentFilters();
    const modals = useResidentModals();
    const pages = useResidentPages({ ehpadId, viewMode: residentViewMode, filters: filters.filters, onError, setSelectedPatientId });
    const loaded = useLoadedPatients({ ehpadId, setRows: pages.setRows, syncPatient: modals.syncPatient, onError });
    const saving = useResidentSaving({ ehpadId, loaded, onError });
    const actions = useResidentActions({ ehpadId, pages, loaded, saving, modals, setSelectedPatientId, setSearch: filters.setSearch, onError });
    const { drafts, setDrafts, loadPatientDetail } = loaded;
    const { loadRows } = pages;

    useEffect(() => {
        setDrafts({});
        loadRows();
    }, [loadRows, pharmacyId, setDrafts]);

    const filteredRows = useMemo(() => filterResidentRows(pages.rows, drafts, filters.filters), [drafts, filters.filters, pages.rows]);
    const selectedEhpad = ehpads.find(ehpad => samePatient(ehpad.id, ehpadId));
    const subcategories = useMemo(() => selectedEhpad?.productionSubcategories ?? [], [selectedEhpad?.productionSubcategories]);
    const selectedRow = useResidentSelection({
        filteredRows, selectedPatientId, setSelectedPatientId,
        subcategory: filters.subcategory, setSubcategory: filters.setSubcategory, subcategories,
    });

    useEffect(() => {
        if (residentViewMode !== "vertical" || !selectedPatientId || drafts[selectedPatientId]) return;
        const selected = pages.rows.find(row => samePatient(row.patientId, selectedPatientId));
        if (selected?.billingEvents && selected?.vaccinationEvents) return;
        loadPatientDetail(selectedPatientId);
    }, [drafts, loadPatientDetail, pages.rows, residentViewMode, selectedPatientId]);

    const currentOf = row => (row ? (drafts[row.patientId] || pages.rows.find(item => samePatient(item.patientId, row.patientId)) || row) : null);
    const selectedCurrent = selectedRow ? (drafts[selectedRow.patientId] || selectedRow) : null;
    return {
        ...modals, ...actions,
        ehpads, ehpadId, setEhpadId, selectedEhpad, selectedEhpadName: selectedEhpad?.nom || "", selectedEhpadSubcategories: subcategories,
        residentViewMode, setResidentViewMode,
        search: filters.search, setSearch: filters.setSearch,
        residentStatusFilter: filters.status, setResidentStatusFilter: filters.setStatus,
        residentSubcategoryFilter: filters.subcategory, setResidentSubcategoryFilter: filters.setSubcategory,
        rows: pages.rows, loading: pages.loading, loadingMorePatients: pages.loadingMore, hasMorePatients: pages.hasMore,
        patientTotal: pages.total, loadedPatientCount: pages.rows.length, loadRows,
        handlePatientsScroll: event => {
            const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
            if (scrollHeight - scrollTop - clientHeight < 240) pages.loadNext();
        },
        drafts, setDrafts, detailLoading: loaded.detailLoading, replaceLoadedPatient: loaded.replaceLoadedPatient, loadPatientDetail,
        updateRowDraft: loaded.updateRowDraft, cancelRow: loaded.cancelRow,
        savingIds: saving.savingIds, saveRow: saving.saveRow, mergeConflict: saving.mergeConflict,
        setMergeConflict: saving.setMergeConflict, confirmMerge: saving.confirmMerge,
        filteredRows, selectedPatientId, setSelectedPatientId, selectedRow, selectedCurrent,
        selectedDirty: Boolean(selectedRow && drafts[selectedRow.patientId]),
        selectedSaving: Boolean(selectedRow && saving.savingIds[selectedRow.patientId]),
        selectedHasDetails: Boolean(selectedCurrent?.billingEvents && selectedCurrent?.vaccinationEvents),
        identityCurrent: currentOf(modals.identityRow),
        documentCurrent: currentOf(modals.documentRow),
    };
}

// Chaque onglet (crédits, tuteurs) se recharge quand il est affiché ou quand la pharmacie change.
function useSectionLoader(active, needsPharmacy, pharmacyId, load) {
    useEffect(() => {
        if (!active || needsPharmacy) return;
        load();
    }, [active, load, needsPharmacy, pharmacyId]);
}

function useAdministration() {
    const { user, selectedPharmacy } = useAuth();
    const [error, setError] = useState("");
    const [activeSection, setActiveSection] = useState("residents");
    const isSuperAdmin = user?.role === "SUPER_ADMIN";
    const needsPharmacy = isSuperAdmin && !selectedPharmacy;
    const pharmacyId = selectedPharmacy?.id;
    const residents = useResidentWorkspace({ needsPharmacy, pharmacyId, onError: setError });
    const credits = useEhpadCredits(setError);
    const guardians = useGuardianCurators(setError);
    useSectionLoader(activeSection === "credits", needsPharmacy, pharmacyId, credits.load);
    useSectionLoader(activeSection === "guardians", needsPharmacy, pharmacyId, guardians.load);
    const proposals = useAdministrativeProposals({
        ehpadId: residents.ehpadId,
        disabled: needsPharmacy,
        onError: setError,
        onReviewed: async proposal => {
            if (proposal.patientId) await residents.loadPatientDetail(proposal.patientId);
            if (residents.residentViewMode === "horizontal") await residents.loadRows();
        },
    });
    const { load: loadProposals } = proposals;
    useEffect(() => {
        if (activeSection === "residents") loadProposals();
    }, [activeSection, loadProposals, pharmacyId]);
    const documents = usePatientDocuments({ replaceLoadedPatient: residents.replaceLoadedPatient, onError: setError });
    const report = useAdministrationReport({ ehpadId: residents.ehpadId, ehpadName: residents.selectedEhpadName, onError: setError });
    const workbook = useAdministrationImport({
        onError: setError,
        onApplied: async () => {
            residents.setDrafts({});
            credits.setDrafts({});
            guardians.setDrafts({});
            await Promise.all([residents.loadRows(), credits.load(), guardians.load()]);
        },
    });
    const { eventsModal, chronicProductRow } = residents;

    return {
        ...residents,
        user, error, setError, activeSection, setActiveSection, isSuperAdmin, needsPharmacy,
        credits, guardians, proposals,
        documentBusyIds: documents.busyIds,
        uploadPatientDocument: documents.upload,
        openPatientDocument: documents.open,
        deletePatientDocument: documents.remove,
        reportModalOpen: report.modalOpen, setReportModalOpen: report.setModalOpen, reportRows: report.rows,
        reportLoading: report.loading, openReportModal: report.open, updateReportRow: report.updateRow,
        downloadAdministrationReport: report.download,
        importFiles: workbook.files, setImportFiles: workbook.setFiles, setImportMode: workbook.setMode,
        importReport: workbook.report, setImportReport: workbook.setReport, importing: workbook.importing,
        importWorkbook: workbook.importWorkbook,
        refreshEventPatient: async (patientId = eventsModal?.row?.patientId || chronicProductRow?.patientId) => {
            if (patientId) await residents.loadPatientDetail(patientId);
        },
    };
}

const ADMINISTRATION_ROW_CSS = `
                .administration-patient-row--not-invoiced > td,
                .administration-patient-row--inactive > td {
                    background: #dbeafe !important;
                }
                .administration-patient-row--not-invoiced.administration-patient-row--inactive > td {
                    background: #fee2e2 !important;
                }
                .administration-patient-row--not-invoiced > td,
                .administration-patient-row--not-invoiced > td *,
                .administration-patient-row--inactive > td,
                .administration-patient-row--inactive > td * {
                    color: #1e3a8a !important;
                }
                .administration-patient-row--not-invoiced.administration-patient-row--inactive > td,
                .administration-patient-row--not-invoiced.administration-patient-row--inactive > td * {
                    color: #7f1d1d !important;
                }
                .administration-patient-row--not-invoiced > td:first-child,
                .administration-patient-row--inactive > td:first-child {
                    box-shadow: inset 5px 0 0 #2563eb;
                }
                .administration-patient-row--not-invoiced.administration-patient-row--inactive > td:first-child {
                    box-shadow: inset 5px 0 0 #dc2626;
                }
                @media (max-width: 900px) {
                    .administration-vertical-workspace {
                        grid-template-columns: 1fr !important;
                    }
                    .administration-vertical-sidebar {
                        position: static !important;
                    }
                }
            `;

export default function Administration() {
    const view = useAdministration();
    const { activeSection, credits, error, guardians, needsPharmacy, proposals, residentViewMode } = view;

    if (needsPharmacy) {
        return (
            <div style={centeredStyle}>
                <h2>Sélectionnez une pharmacie</h2>
                <p>Choisissez d’abord une pharmacie dans le menu de gauche.</p>
            </div>
        );
    }

    return (
        <div style={{ minHeight: "100%", background: "#f8fafc", padding: "28px" }}>
            <style>{ADMINISTRATION_ROW_CSS}</style>
            <div style={{ marginBottom: "22px" }}>
                <h1 style={{ margin: 0, fontSize: "28px", color: "#111827" }}>Administration</h1>
                <p style={{ color: "#6b7280", margin: "6px 0 0" }}>Suivi administratif des résidents et des EHPADs.</p>
            </div>

            {renderImportPanel(view)}

            {renderTabs(view)}

            {error && <p role="alert" style={errorStyle}>{error}</p>}

            {activeSection === "residents" && (<>
                <AdministrativeProposalsPanel
                    proposals={proposals.proposals}
                    loading={proposals.loading}
                    busyIds={proposals.busyIds}
                    onAccept={proposal => proposals.review(proposal, "accept")}
                    onReject={proposal => proposals.review(proposal, "reject")}
                />
                {renderResidentToolbar(view)}
                {residentViewMode === "horizontal" ? renderHorizontalTable(view) : renderVerticalWorkspace(view)}
            </>)}

            {activeSection === "credits" && (
                <EhpadCreditsTable
                    rows={credits.rows}
                    drafts={credits.drafts}
                    additions={credits.additions}
                    loading={credits.loading}
                    savingIds={credits.savingIds}
                    onChange={credits.updateDraft}
                    onAdditionChange={credits.setAddition}
                    onAdd={credits.addAmount}
                    onSave={credits.saveRow}
                    onCancel={credits.cancelDraft}
                />
            )}

            {activeSection === "guardians" && (
                <GuardianCuratorTable
                    rows={guardians.rows}
                    drafts={guardians.drafts}
                    loading={guardians.loading}
                    savingIds={guardians.savingIds}
                    onChange={guardians.updateDraft}
                    onSave={guardians.saveRow}
                    onCancel={guardians.cancelDraft}
                />
            )}

            {renderModals(view)}
        </div>
    );
}
